import { useState } from "react";
import { Flag } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { REPORT_REASONS, submitReport } from "@/lib/reports";
import { azError } from "@/lib/az-errors";

type Props = {
  listingId?: string | null;
  reportedUserId?: string | null;
};

/** Elan üçün səbəb seçimli şikayət pəncərəsi. */
export function ReportDialog({ listingId, reportedUserId }: Props) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<string>("");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!reason) {
      toast.error("Zəhmət olmasa şikayət səbəbini seçin");
      return;
    }
    if (reason === "diger" && details.trim().length < 5) {
      toast.error("Digər səbəb üçün ətraflı yazın");
      return;
    }
    setBusy(true);
    try {
      await submitReport({
        listingId: listingId ?? null,
        reportedUserId: reportedUserId ?? null,
        reason,
        details,
      });
      toast.success("Şikayətiniz göndərildi. Admin komandası yoxlayacaq.");
      setOpen(false);
      setReason("");
      setDetails("");
    } catch (e) {
      toast.error(azError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-2xl border border-border py-3 text-sm font-semibold text-muted-foreground transition-colors hover:text-destructive"
        >
          <Flag className="size-4" /> Şikayət et
        </button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto rounded-3xl sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Nəyə görə şikayət edirsiniz?</DialogTitle>
          <DialogDescription>
            Səbəbi seçin — şikayətiniz admin panelə düşəcək və yoxlanılacaq.
          </DialogDescription>
        </DialogHeader>

        <RadioGroup value={reason} onValueChange={setReason} className="gap-2">
          {REPORT_REASONS.map((r) => (
            <Label
              key={r.value}
              htmlFor={`report-${r.value}`}
              className="flex cursor-pointer items-center gap-3 rounded-2xl border border-border p-3 text-sm font-medium has-[button[data-state=checked]]:border-primary"
            >
              <RadioGroupItem value={r.value} id={`report-${r.value}`} />
              <span>{r.label}</span>
            </Label>
          ))}
        </RadioGroup>

        <Textarea
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          placeholder="Ətraflı yazın (istəyə bağlı)"
          className="min-h-24 rounded-2xl"
        />

        <Button className="w-full rounded-2xl" onClick={send} disabled={busy}>
          {busy ? "Göndərilir…" : "Şikayəti göndər"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
